import React, { useRef, useState } from "react";
import ImagePreviewModal from "./ImagePreviewModal";

const fileExtension = (name: string) => (name?.split(".").pop() || "").toLowerCase();

const FILE_COLORS: Record<string, { bg: string; fg: string; label: string }> = {
    pdf: { bg: "#fee2e2", fg: "#dc2626", label: "PDF" },
    doc: { bg: "#dbeafe", fg: "#1d4ed8", label: "DOC" },
    docx: { bg: "#dbeafe", fg: "#1d4ed8", label: "DOC" },
    xls: { bg: "#dcfce7", fg: "#15803d", label: "XLS" },
    xlsx: { bg: "#dcfce7", fg: "#15803d", label: "XLS" },
    ppt: { bg: "#ffedd5", fg: "#c2410c", label: "PPT" },
    pptx: { bg: "#ffedd5", fg: "#c2410c", label: "PPT" },
};

const ChatFileCard: React.FC<{ url: string; fileName?: string }> = ({ url, fileName }) => {
    const ext = fileExtension(fileName || url);
    const style = FILE_COLORS[ext] || { bg: "#f3f4f6", fg: "#374151", label: ext ? ext.toUpperCase().slice(0, 4) : "FILE" };
    return (
        <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2"
            style={{ minWidth: 220, maxWidth: 280, textDecoration: "none" }}
        >
            <div style={{ width: 38, height: 38, borderRadius: "8px", background: style.bg, color: style.fg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: 800, flexShrink: 0 }}>
                {style.label}
            </div>
            <div style={{ minWidth: 0 }}>
                <p className="m-0" style={{ fontSize: "13px", fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {fileName || "Document"}
                </p>
                <p className="m-0" style={{ fontSize: "11px", color: "#6b7280" }}>Tap to open</p>
            </div>
        </a>
    );
};

const GOOGLE_MAP_KEY = process.env.REACT_APP_GOOGLE_MAP_KEY;

const ChatLocationCard: React.FC<{ lat: number; lng: number; address?: string }> = ({ lat, lng, address }) => {
    const mapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;
    const staticMapUrl = GOOGLE_MAP_KEY
        ? `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=15&size=280x130&markers=color:red%7C${lat},${lng}&key=${GOOGLE_MAP_KEY}`
        : null;
    // Static Maps needs its own API enablement separate from JS/Places APIs
    // the rest of the app uses — if that thumbnail 404s/errors, fall back to
    // an embedded live Google Maps frame (needs no API key at all) instead
    // of a broken image.
    const [staticMapFailed, setStaticMapFailed] = useState(false);
    const embedUrl = `https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed`;

    return (
        <div style={{ maxWidth: 280 }}>
            {staticMapUrl && !staticMapFailed ? (
                <a href={mapsUrl} target="_blank" rel="noreferrer">
                    <img
                        src={staticMapUrl}
                        alt="Map location"
                        style={{ width: "100%", borderRadius: "10px 10px 0 0", display: "block" }}
                        onError={() => setStaticMapFailed(true)}
                    />
                </a>
            ) : (
                <iframe
                    title="Map location"
                    src={embedUrl}
                    width="100%"
                    height="130"
                    style={{ border: 0, borderRadius: "10px 10px 0 0", display: "block" }}
                    loading="lazy"
                />
            )}
            <a href={mapsUrl} target="_blank" rel="noreferrer" style={{ display: "block", textDecoration: "none", padding: "8px 2px 0" }}>
                <p className="m-0" style={{ fontSize: "12px", fontWeight: 600, color: "#1d4ed8" }}>📍 Open in Google Maps</p>
                {address && (
                    <p className="m-0" style={{ fontSize: "11px", color: "#6b7280", marginTop: "2px" }}>{address}</p>
                )}
            </a>
        </div>
    );
};

// Fixed bar heights, progress-based fill — same approach as the merchant
// app's chat waveform (no real frequency analysis, just a stylized meter).
const AUDIO_BARS = [8, 14, 10, 18, 12, 22, 10, 16, 8, 20, 14, 24, 10, 18, 12, 20, 8, 14, 10, 18, 12, 22, 10, 16, 8, 20];

const formatTime = (t: number) => {
    if (!isFinite(t) || isNaN(t)) return "0:00";
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
};

const ChatAudioPlayer: React.FC<{ url: string }> = ({ url }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const audioRef = useRef<HTMLAudioElement>(null);

    const togglePlay = () => {
        const audio = audioRef.current;
        if (!audio) return;
        if (isPlaying) audio.pause();
        else audio.play();
        setIsPlaying(!isPlaying);
    };

    return (
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 220 }}>
            <audio
                ref={audioRef}
                src={url}
                onTimeUpdate={() => {
                    const audio = audioRef.current;
                    if (audio && audio.duration) {
                        setProgress((audio.currentTime / audio.duration) * 100);
                        setCurrentTime(audio.currentTime);
                    }
                }}
                onLoadedMetadata={() => { if (audioRef.current) setDuration(audioRef.current.duration); }}
                onEnded={() => { setIsPlaying(false); setProgress(0); }}
                style={{ display: "none" }}
            />
            <button
                onClick={togglePlay}
                style={{
                    width: 32, height: 32, borderRadius: "50%", background: "#1d4ed8", color: "#fff",
                    border: "none", display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: "pointer", flexShrink: 0, fontSize: "13px",
                }}
            >
                {isPlaying ? "⏸" : "▶"}
            </button>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 24 }}>
                    {AUDIO_BARS.map((h, i) => {
                        const active = progress > (i / AUDIO_BARS.length) * 100;
                        return (
                            <div
                                key={i}
                                style={{ width: 3, height: h, borderRadius: 2, background: active ? "#1d4ed8" : "#c7d2fe", transition: "background 0.15s" }}
                            />
                        );
                    })}
                </div>
                <div style={{ fontSize: "10px", color: "#9ca3af", marginTop: "2px" }}>
                    {formatTime(currentTime)} / {formatTime(duration)}
                </div>
            </div>
        </div>
    );
};

// Custom-controlled video bubble — the bare browser `controls` UI looked
// mismatched/cropped against portrait clips, so this mirrors ChatAudioPlayer's
// approach: no native chrome, just a tap-to-play overlay and a slim seek bar.
const ChatVideoPlayer: React.FC<{ url: string }> = ({ url }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);

    const togglePlay = () => {
        const video = videoRef.current;
        if (!video) return;
        if (isPlaying) video.pause();
        else video.play();
        setIsPlaying(!isPlaying);
    };

    const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
        const video = videoRef.current;
        if (!video || !video.duration) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
        video.currentTime = ratio * video.duration;
    };

    return (
        <div
            onClick={togglePlay}
            style={{ position: "relative", width: 240, maxWidth: "100%", borderRadius: "12px", overflow: "hidden", background: "#000", boxShadow: "0 2px 8px rgba(0,0,0,0.15)", cursor: "pointer" }}
        >
            <video
                ref={videoRef}
                src={url}
                playsInline
                style={{ width: "100%", maxHeight: "320px", display: "block", objectFit: "contain", background: "#000" }}
                onTimeUpdate={() => {
                    const v = videoRef.current;
                    if (v && v.duration) {
                        setProgress((v.currentTime / v.duration) * 100);
                        setCurrentTime(v.currentTime);
                    }
                }}
                onLoadedMetadata={() => { if (videoRef.current) setDuration(videoRef.current.duration); }}
                onEnded={() => { setIsPlaying(false); setProgress(0); }}
            />
            {!isPlaying && (
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div style={{ width: 48, height: 48, borderRadius: "50%", background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <span style={{ color: "#fff", fontSize: "18px", marginLeft: "3px" }}>▶</span>
                    </div>
                </div>
            )}
            <div
                onClick={(e) => e.stopPropagation()}
                style={{ position: "absolute", left: 0, right: 0, bottom: 0, padding: "6px 8px", background: "linear-gradient(transparent, rgba(0,0,0,0.65))", display: "flex", alignItems: "center", gap: 8 }}
            >
                <button
                    onClick={togglePlay}
                    style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", fontSize: "13px", padding: 0, lineHeight: 1 }}
                >
                    {isPlaying ? "⏸" : "▶"}
                </button>
                <div onClick={handleSeek} style={{ flex: 1, height: 4, background: "rgba(255,255,255,0.35)", borderRadius: 2, cursor: "pointer" }}>
                    <div style={{ width: `${progress}%`, height: "100%", background: "#fff", borderRadius: 2 }} />
                </div>
                <span style={{ color: "#fff", fontSize: "10px", minWidth: 34, textAlign: "right" }}>
                    {formatTime(currentTime)} / {formatTime(duration)}
                </span>
            </div>
        </div>
    );
};

// Wraps the query substring in <mark> — case-insensitive, same approach as
// the merchant app's chat search highlight.
const highlightText = (text: string, query?: string) => {
    if (!query) return text;
    const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi"));
    return parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase()
            ? <mark key={i} style={{ background: "#facc15", color: "#111827", borderRadius: "2px", padding: "0 1px" }}>{part}</mark>
            : part
    );
};

// Renders one chat message's body by type — shared by ChatPage.tsx (the
// standalone admin chat module) and ProfileView.tsx (per-user chat panel)
// so IMAGE/VIDEO/AUDIO/FILE/LOCATION/TEXT stay in sync in one place.
const ChatMessageContent: React.FC<{ message: any; searchQuery?: string }> = ({ message: m, searchQuery }) => {
    const [previewOpen, setPreviewOpen] = useState(false);

    switch (m.message_type) {
        case "IMAGE":
        case "CAMERA":
            return (
                <>
                    <img
                        src={m.message}
                        alt="attachment"
                        onClick={() => setPreviewOpen(true)}
                        style={{ maxWidth: "100%", borderRadius: "10px", display: "block", cursor: "pointer" }}
                    />
                    {previewOpen && (
                        <ImagePreviewModal selectedImage={m.message} onclose={() => setPreviewOpen(false)} />
                    )}
                </>
            );
        case "VIDEO":
            return <ChatVideoPlayer url={m.message} />;
        case "AUDIO":
            return <ChatAudioPlayer url={m.message} />;
        case "FILE":
            return <ChatFileCard url={m.message} fileName={m.file_name} />;
        case "LOCATION": {
            // Sent as a JSON string: {"lat":..,"long":..,"address":".."} —
            // fall back to the raw text if it ever arrives in some other shape.
            try {
                const loc = JSON.parse(m.message);
                const lat = Number(loc.lat);
                const lng = Number(loc.long ?? loc.lng);
                if (!isNaN(lat) && !isNaN(lng)) {
                    return <ChatLocationCard lat={lat} lng={lng} address={loc.address} />;
                }
            } catch {
                // not JSON — try the older "lat,lng" plain-text shape
                const [lat, lng] = String(m.message || "").split(",").map((s: string) => s.trim());
                if (lat && lng && !isNaN(Number(lat)) && !isNaN(Number(lng))) {
                    return <ChatLocationCard lat={Number(lat)} lng={Number(lng)} />;
                }
            }
            return <p className="m-0" style={{ fontSize: "13px", color: "#111827" }}>📍 {m.message}</p>;
        }
        default:
            return <p className="m-0" style={{ fontSize: "14px", color: "#111827", whiteSpace: "pre-wrap" }}>{highlightText(m.message || "", searchQuery)}</p>;
    }
};

export default ChatMessageContent;
